import { reactive } from '@vue/composition-api';

import hokkaidoAirportReportApi from '@/apis/externalReports/hokkaidoAirport/hokkaido_airport_report';

import {
  ReportState,
  SearchState,
} from '@/models/externalReports/hokkaidoAirport/hokkaidoAirportMap';
import {
  deselectReportItems,
  convSearchParamTsTo,
  convReport,
  loadHokkaidoAirportMtxs,
} from '@/components/ExternalReports/HokkaidoAirport/utils';
import type {
  HokkaidoAirportReportExt,
  HokkaidoAirportPhotoOrder,
  HokkaidoAirportReportPhotoExt,
} from '@/models/externalReports/hokkaidoAirport/hokkaidoAirportReport';
import { createInjection } from '@/lib/createInjection';
import type { HokkaidoAirportReportPhotoParams } from '@/models/apis/externalReports/hokkaidoAirport/hokkaidoAirportReportRequest';
import { dtFormat } from '@/lib/dateHelper';
import { getImageCandidate } from '@/lib/imageHelper';
import * as ExifReader from 'exifreader';

interface InjectedState {
  reportState: ReportState;
  searchState: SearchState;
}

export interface UseHokkaidoAirportReportsResult {
  reportState: ReportState;
  searchReports: () => Promise<void>;
  selectReport: (reportId: number) => Promise<void>;
  deselectReport: (reportId: number) => boolean;
  getSelectedReport: () => HokkaidoAirportReportExt | null;
  getSelectedPhoto: () => HokkaidoAirportReportPhotoExt | null;
  getShowPhotos: () => HokkaidoAirportReportPhotoExt[] | null;
  deselectSelectedReportPhotos: () => void;
  resetSelectedPhotoSamePos: () => void;
  clearReports: () => void;
  updateReportPhotosOrder: (photoId: number, newOrders: HokkaidoAirportPhotoOrder[]) => Promise<void>;
  resetReportPhotosOrder: () => Promise<void>;
  updateComment: (photoId: number, comment: string) => Promise<void>;
  deletePhoto: (photoId: number) => Promise<void>;
  updateReportPhotoSavedImages: (photos: HokkaidoAirportReportPhotoExt[]) => void;
  sendImageCandidate: (file: File, photoType: string) => Promise<HokkaidoAirportReportPhotoExt | null>;
}

const { provide, inject } = createInjection<InjectedState>('hokkaidoAirportMap/useHokkaidoAirportReports');

export const getLocation = async(file: File): Promise<{ lat: number; lon: number }> => {
  try {
    const buffer = await file.arrayBuffer();
    const tags = ExifReader.load(buffer);

    const latTag = tags.GPSLatitude;
    const lonTag = tags.GPSLongitude;

    if (latTag && lonTag) {
      const lat = Number(latTag.description);
      const lon = Number(lonTag.description);
      return { lat, lon };
    } else {
      return { lat: 0, lon: 0 };
    }
  } catch (err) {
    return { lat: 0, lon: 0 };
  }
};

export function provideHokkaidoAirportReports(searchState: SearchState): InjectedState {
  const reportState = reactive<ReportState>({
    reports: [],
    saveLocation: null,
  });
  const injectedState = { reportState, searchState };
  provide(injectedState);
  return injectedState;
}

export function useHokkaidoAirportReports(defaultInjectedState?: InjectedState): UseHokkaidoAirportReportsResult {
  const injectedState = defaultInjectedState ?? inject();
  const reportState = injectedState.reportState;
  const searchState = injectedState.searchState;

  const searchReports = async(): Promise<void> => {
    const tsFrom = new Date(searchState.searchConds.dateFrom);
    tsFrom.setHours(0, 0, 0, 0);
    const reqParams = {
      ts_from: tsFrom,
      ts_to: convSearchParamTsTo(searchState.searchConds.dateTo),
      han_name: searchState.searchConds.hanName,
    };
    const hokkaidoAirportReports = await hokkaidoAirportReportApi.index(reqParams);
    reportState.reports = hokkaidoAirportReports.map((report) => {
      return convReport(report);
    });
  };

  const getSelectedReport = (): HokkaidoAirportReportExt | null => {
    return reportState.reports.find(e => e.isSelected) ?? null;
  };

  const getSelectedPhoto = (): HokkaidoAirportReportPhotoExt | null => {
    const selectedReport = getSelectedReport();
    if (!selectedReport) return null;

    return selectedReport.photosExt.find(photo => photo.isSelected) ?? null;
  };

  const getShowPhotos = (): HokkaidoAirportReportPhotoExt[] | null => {
    const selectedPhoto = getSelectedPhoto();
    if (!selectedPhoto) return null;

    if (reportState.saveLocation) return selectedPhoto.samePosPhotos;

    const selectedReport = getSelectedReport();
    if (!selectedReport) return null;

    return selectedReport.photosExt.filter(photo => photo.photoType === selectedPhoto.photoType);
  };

  const resetSelectedPhotoSamePos = (): void => {
    const selectedReport = getSelectedReport();
    if (!selectedReport) return;

    const photo = getSelectedPhoto();
    if (!photo) return;

    const isSamePos = (e: typeof photo) =>
      roundToSixDecimals(e.lat) === roundToSixDecimals(photo.lat) &&
      roundToSixDecimals(e.lon) === roundToSixDecimals(photo.lon) &&
      e.photoType === photo.photoType;

    const samePosPhotos = selectedReport.photosExt
      .filter(isSamePos)
      .sort((a, b) => a.dispOrder - b.dispOrder);
    samePosPhotos.map(photo => {
      if (!photo.savedImage) {
        photo.savedImage = photo.samePosPhotos.find(p => p.id === photo.id)?.savedImage ?? photo.savedImage;
      }
    });

    selectedReport.photosExt.forEach(e => {
      if (isSamePos(e)) {
        e.samePosPhotos = samePosPhotos;
      }
    });
  };

  const roundToSixDecimals = (num: number): number => {
    const rounded = Math.round(num * 1000000) / 1000000;
    return rounded;
  };

  const selectReport = async(reportId: number): Promise<void> => {
    const prev = getSelectedReport();
    if (prev && prev.id !== reportId) {
      deselectReportItems(prev);
    }
    for (const report of reportState.reports) {
      report.isSelected = report.id === reportId;
      if (!report.isSelected) continue;

      if (!report.isMtxsLoaded) {
        await loadHokkaidoAirportMtxs([report]);
      }
    }
  };

  const updateReportPhotoSavedImages = (photos: HokkaidoAirportReportPhotoExt[]): void => {
    const selectedReport = getSelectedReport();
    if (!selectedReport) return;

    photos.forEach(photo => {
      if (!photo.savedImage) return;
      const target = selectedReport.photosExt.find(p => p.id === photo.id);
      if (!target) return;

      if (target.savedImage !== photo.savedImage) {
        target.savedImage = photo.savedImage;
      }

      selectedReport.photosExt.forEach(candidate => {
        const isSamePos =
          roundToSixDecimals(candidate.lat) === roundToSixDecimals(photo.lat) &&
          roundToSixDecimals(candidate.lon) === roundToSixDecimals(photo.lon) &&
          candidate.photoType === photo.photoType;
        if (!isSamePos) return;

        if (candidate.id === photo.id && candidate.savedImage !== photo.savedImage) {
          candidate.savedImage = photo.savedImage;
        }
      });
    });
  };

  const sendImageCandidate = async(file: File, photoType: string): Promise<HokkaidoAirportReportPhotoExt | null> => {
    const selectedReport = getSelectedReport();
    if (!selectedReport) return null;

    const imageLocation = reportState.saveLocation ?? await getLocation(file);
    const imageCandidate = await getImageCandidate(file);
    if (!imageCandidate) return null;

    const reqObj: HokkaidoAirportReportPhotoParams = {
      ts: dtFormat(new Date()),
      lat: roundToSixDecimals(imageLocation.lat),
      lon: roundToSixDecimals(imageLocation.lon),
      photo_type: photoType,
      comment: '',
      image: imageCandidate.file,
    };
    const newPhoto = await hokkaidoAirportReportApi.addPhoto(selectedReport.id, reqObj);
    selectedReport.photosExt.forEach(p => { p.isSelected = false; });
    const savedImage = URL.createObjectURL(imageCandidate.file);
    const photo: HokkaidoAirportReportPhotoExt = {
      ...newPhoto,
      isSelected: true,
      hanName: selectedReport.hanName ?? '',
      airportName: selectedReport.airportName ?? '',
      standbyType: selectedReport.standbyType ?? '',
      savedImage: savedImage,
      samePosPhotos: selectedReport.photosExt.filter(
        photo => roundToSixDecimals(photo.lat) === roundToSixDecimals(imageLocation.lat) &&
                  roundToSixDecimals(photo.lon) === roundToSixDecimals(imageLocation.lon) &&
                  photo.photoType === photoType,
      ),
    };
    selectedReport.photosExt.push(photo);
    resetSelectedPhotoSamePos();
    return photo;
  };

  const updateComment = async(photoId: number, comment: string): Promise<void> => {
    const selectedReport = getSelectedReport();
    if (!selectedReport) return;

    const { data: photoResponse } = await hokkaidoAirportReportApi.updatePhotoComment(selectedReport.id, photoId, comment);
    const updatedPhoto = selectedReport.photosExt.find(p => p.id === photoId);
    if (!updatedPhoto) return;
    updatedPhoto.comment = photoResponse.comment;
    resetSelectedPhotoSamePos();
  };

  const deletePhoto = async(photoId: number): Promise<void> => {
    const selectedReport = getSelectedReport();
    if (!selectedReport) return;

    const deletePhoto = selectedReport.photosExt.find(photo => photo.id === photoId);
    if (!deletePhoto) return;

    await hokkaidoAirportReportApi.deletePhoto(selectedReport.id, photoId);
    let firstSelected = false;
    selectedReport.photosExt = selectedReport.photosExt.filter(p => p.id !== deletePhoto.id).map(photo => {
      if (photo.photoType === deletePhoto.photoType &&
          (!reportState.saveLocation ||
          (photo.lat === deletePhoto.lat && photo.lon === deletePhoto.lon))) {
        if (!firstSelected) {
          firstSelected = true;
          return { ...photo, isSelected: true };
        }
        return { ...photo, isSelected: false };
      }
      return photo;
    });
  };

  const updateReportPhotosOrder = async(photoId: number, newOrders: HokkaidoAirportPhotoOrder[]): Promise<void> => {
    const selectedReport = getSelectedReport();
    if (!selectedReport) return;

    await hokkaidoAirportReportApi.updatePhotosOrder(selectedReport.id, newOrders);
    selectedReport.photosExt.map(photo => {
      photo.isSelected = photo.id === photoId;
    });
  };

  const resetReportPhotosOrder = async(): Promise<void> => {
    const selectedReport = getSelectedReport();
    if (!selectedReport) return;

    const { data: newOrders } = await hokkaidoAirportReportApi.getPhotoOrders(selectedReport.id);
    const newOrderPhotosExt = selectedReport.photosExt.map(photo => {
      const newOrder = newOrders.find(e => e.id === photo.id)?.disp_order;
      if (newOrder !== undefined) {
        photo.dispOrder = newOrder;
      }
      return photo;
    });
    selectedReport.photosExt = newOrderPhotosExt;
    resetSelectedPhotoSamePos();
  };

  const deselectReport = (reportId: number): boolean => {
    const report = getSelectedReport();
    if (!report || report.id !== reportId) return false;

    report.isSelected = false;
    deselectReportItems(report);
    return true;
  };

  const deselectSelectedReportPhotos = (): void => {
    const report = getSelectedReport();
    if (!report) return;

    deselectReportItems(report);
  };

  const clearReports = (): void => {
    reportState.reports.forEach(report => {
      for (const photo of report.photosExt) {
        if (photo.savedImage) {
          URL.revokeObjectURL(photo.savedImage);
        }
      }
    });
    reportState.reports = [];
  };

  return {
    reportState,
    searchReports,
    getSelectedReport,
    getSelectedPhoto,
    getShowPhotos,
    selectReport,
    deselectReport,
    deselectSelectedReportPhotos,
    clearReports,
    resetSelectedPhotoSamePos,
    updateReportPhotosOrder,
    updateComment,
    deletePhoto,
    resetReportPhotosOrder,
    updateReportPhotoSavedImages,
    sendImageCandidate,
  };
}
